/*
 * Lipid trends for the trends screen (synthetic "Patient B", seven tests
 * Jan 23 → Dec 24) and the inline SVG line chart that draws them, with shaded
 * normal / borderline / high bands, labelled points, a <title>/<desc> and a
 * visually hidden data table.
 */

export const DATES = ['Jan 23', 'Jun 23', 'Jan 24', 'May 24', 'Aug 24', 'Sep 24', 'Dec 24'];

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/* 'Jan 24' → months since Jan 2000. */
function monthNumber(label) {
  const [mon, yy] = label.split(' ');
  return Number(yy) * 12 + MONTHS.indexOf(mon);
}

/* Index of the test closest to 12 months before the latest one (Jan 24 for Dec 24). */
export const YEAR_AGO_INDEX = (() => {
  const latest = monthNumber(DATES[DATES.length - 1]);
  let best = 0;
  DATES.forEach((d, i) => {
    if (Math.abs(latest - monthNumber(d) - 12) < Math.abs(latest - monthNumber(DATES[best]) - 12)) best = i;
  });
  return best;
})();

/*
 * The change between two points of a marker's series, so every number shown
 * (chart badge, Change tile, phone and WhatsApp mocks) comes from the data.
 * `good` is true when the marker moved in its healthy direction.
 */
export function seriesChange(marker, fromIndex, toIndex = marker.values.length - 1) {
  const from = marker.values[fromIndex];
  const to = marker.values[toIndex];
  const down = to < from;
  return {
    from,
    to,
    down,
    amount: Math.abs(to - from),
    percent: Math.round((Math.abs(to - from) / from) * 100),
    good: down === (marker.better === 'lower'),
  };
}

export const changeOverYear = (marker) => seriesChange(marker, YEAR_AGO_INDEX);
export const changeSinceLast = (marker) => seriesChange(marker, marker.values.length - 2);

/* Band tones reuse the status colours; every band also has a word. */
const BAND_FILL = {
  normal: 'rgba(31, 164, 99, 0.13)',
  near: 'rgba(31, 164, 99, 0.05)',
  borderline: 'rgba(242, 176, 31, 0.2)',
  high: 'rgba(229, 72, 77, 0.13)',
};

const LINE_COLOUR = { normal: '#1fa463', borderline: '#b07d00', high: '#e5484d' };

export const MARKERS = [
  {
    key: 'total',
    button: 'Total cholesterol',
    name: 'Total Cholesterol',
    unit: 'mg/dL',
    values: [220, 196, 182, 158, 149, 141, 130],
    better: 'lower',
    status: 'normal',
    y: { min: 100, max: 250, ticks: [100, 150, 200, 250] },
    bands: [
      { tone: 'normal', to: 200, label: 'normal below 200' },
      { tone: 'borderline', from: 200, to: 240, label: 'borderline 200 to 239' },
      { tone: 'high', from: 240, label: 'high 240 and above' },
    ],
    trend: { word: 'Improving', note: 'Over last 12 months', good: true },
    range: { word: 'Now in normal range', note: 'Optimal: < 200 mg/dL', good: true },
    meaning:
      'Your total cholesterol has been steadily improving, moving from the borderline range into the normal range. Keep maintaining your current diet and activity levels, and continue regular monitoring.',
  },
  {
    key: 'ldl',
    button: 'LDL',
    name: 'LDL Cholesterol',
    unit: 'mg/dL',
    values: [168, 163, 158, 151, 146, 139, 130],
    better: 'lower',
    status: 'borderline',
    y: { min: 80, max: 180, ticks: [80, 100, 130, 160, 180] },
    // NCEP ATP III LDL bands.
    bands: [
      { tone: 'normal', to: 100, label: 'optimal below 100' },
      { tone: 'near', from: 100, to: 130, label: 'near optimal 100 to 129' },
      { tone: 'borderline', from: 130, to: 160, label: 'borderline high 130 to 159' },
      { tone: 'high', from: 160, label: 'high 160 and above' },
    ],
    trend: { word: 'Improving', note: 'Over last 12 months', good: true },
    range: { word: 'Still above optimal', note: 'Optimal: < 100 mg/dL', good: false },
    meaning:
      'Your LDL, the cholesterol that builds up in blood vessels, has come down from 168 to 130 mg/dL. It is now at the bottom of the borderline high range (130 to 159), so keep up the diet changes and recheck in 3 months.',
  },
  {
    key: 'hdl',
    button: 'HDL',
    name: 'HDL Cholesterol',
    unit: 'mg/dL',
    values: [38, 40, 43, 45, 48, 50, 52],
    better: 'higher',
    status: 'normal',
    y: { min: 30, max: 70, ticks: [30, 40, 50, 60, 70] },
    bands: [
      { tone: 'high', to: 40, label: 'low below 40' },
      { tone: 'normal', from: 40, label: 'normal 40 and above' },
    ],
    trend: { word: 'Improving', note: 'Over last 12 months', good: true },
    range: { word: 'In normal range', note: 'Healthy: ≥ 40 mg/dL', good: true },
    meaning:
      'Your HDL, the "good" cholesterol that clears the rest, has risen from 38 to 52 mg/dL and is now in the healthy range. Regular activity helps keep it there.',
  },
  {
    key: 'triglycerides',
    button: 'Triglycerides',
    name: 'Triglycerides',
    unit: 'mg/dL',
    values: [112, 116, 118, 124, 131, 137, 142],
    better: 'lower',
    status: 'normal',
    y: { min: 80, max: 220, ticks: [100, 150, 200] },
    bands: [
      { tone: 'normal', to: 150, label: 'normal below 150' },
      { tone: 'borderline', from: 150, to: 200, label: 'borderline 150 to 199' },
      { tone: 'high', from: 200, label: 'high 200 and above' },
    ],
    trend: { word: 'Rising', note: 'Still in range; worth watching', good: false },
    range: { word: 'In normal range', note: 'Optimal: < 150 mg/dL', good: true },
    meaning:
      'Your triglycerides are still in the normal range, but they have crept up from 112 to 142 mg/dL. Cutting back on sweets and refined carbs can keep them below the borderline level of 150.',
  },
];

const W = 520;
const H = 230;
const PAD = { left: 40, right: 22, top: 22, bottom: 30 };

export default function TrendChart({ marker }) {
  const { min, max, ticks } = marker.y;
  const plotW = W - PAD.left - PAD.right;
  const plotH = H - PAD.top - PAD.bottom;
  const x = (i) => PAD.left + (plotW * i) / (DATES.length - 1);
  const y = (v) => PAD.top + plotH * (1 - (Math.min(Math.max(v, min), max) - min) / (max - min));
  const line = marker.values.map((v, i) => `${x(i)},${y(v)}`).join(' ');
  const lineColour = LINE_COLOUR[marker.status];
  const first = marker.values[0];
  const last = marker.values[marker.values.length - 1];
  const titleId = `trend-title-${marker.key}`;
  const descId = `trend-desc-${marker.key}`;

  return (
    <div className="tr-chart">
      <svg
        className="tr-chart__svg"
        viewBox={`0 0 ${W} ${H}`}
        role="img"
        aria-labelledby={`${titleId} ${descId}`}
        focusable="false"
      >
        <title id={titleId}>{`${marker.name} (${marker.unit}), ${DATES[0]} to ${DATES[DATES.length - 1]}`}</title>
        <desc id={descId}>
          {`${marker.name} went from ${first} ${marker.unit} in ${DATES[0]} to ${last} ${marker.unit} in ${
            DATES[DATES.length - 1]
          }. Shaded bands: ${marker.bands.map((b) => b.label).join(', ')}.`}
        </desc>
        {marker.bands.map((b) => {
          const top = y(b.to ?? max);
          const bottom = y(b.from ?? min);
          return (
            <rect
              key={b.tone + (b.from ?? '')}
              x={PAD.left}
              y={top}
              width={plotW}
              height={Math.max(0, bottom - top)}
              fill={BAND_FILL[b.tone]}
            />
          );
        })}
        {ticks.map((t) => (
          <text key={t} className="tr-chart__tick" x={PAD.left - 8} y={y(t) + 4} textAnchor="end">
            {t}
          </text>
        ))}
        {DATES.map((d, i) => (
          <text key={d} className="tr-chart__date" x={x(i)} y={H - 8} textAnchor="middle">
            {d}
          </text>
        ))}
        <polyline className="tr-chart__line" points={line} fill="none" stroke={lineColour} />
        {marker.values.map((v, i) => (
          <g key={DATES[i]}>
            <circle cx={x(i)} cy={y(v)} r="4.5" fill="#fff" stroke={lineColour} strokeWidth="2.5" />
            <text className="tr-chart__value" x={x(i)} y={y(v) - 10} textAnchor="middle">
              {v}
            </text>
          </g>
        ))}
      </svg>
      <table className="sr-only">
        <caption>{`${marker.name} results (${marker.unit})`}</caption>
        <thead>
          <tr>
            <th scope="col">Test date</th>
            <th scope="col">Value</th>
          </tr>
        </thead>
        <tbody>
          {marker.values.map((v, i) => (
            <tr key={DATES[i]}>
              <td>{DATES[i]}</td>
              <td>{v}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
